import * as THREE from 'three';
import { terrainHeight } from './terrain';

// Oriented box collider (yaw about Y). Used for every buildable so the player can walk and
// jump on foundation tops and gets blocked by walls and foundation sides.
export interface Box {
  cx: number;
  cy: number;
  cz: number;
  hx: number; // half-extents
  hy: number;
  hz: number;
  yaw: number;
  noBullet?: boolean; // blocks the PLAYER but not bullets/line-of-sight (window frames: you can't jump through, but you can shoot the gaps)
}

// A growable pool of Box objects reused across frames. The collider set is rebuilt every frame for
// both player collision and the bullet hit-test; without pooling that is hundreds of object literals
// per second at 240fps, and the resulting GC sweeps show up as periodic stutter. reset() once per
// frame, then take(...) hands back a recycled Box with the given fields set (no allocation after
// warm-up). The returned boxes are read-only consumers (resolvePlayer/segmentBoxT never mutate them),
// so sharing one pool across all sources in a frame is safe.
export class BoxPool {
  private readonly pool: Box[] = [];
  private n = 0;

  reset(): void {
    this.n = 0;
  }

  take(cx: number, cy: number, cz: number, hx: number, hy: number, hz: number, yaw: number): Box {
    let b = this.pool[this.n];
    if (!b) {
      b = { cx, cy, cz, hx, hy, hz, yaw };
      this.pool[this.n] = b;
    } else {
      b.cx = cx; b.cy = cy; b.cz = cz; b.hx = hx; b.hy = hy; b.hz = hz; b.yaw = yaw;
    }
    b.noBullet = false; // reset the pooled flag: a reused box defaults to a solid (bullet-blocking) collider
    this.n++;
    return b;
  }
}

// Do two yaw-oriented boxes intersect? Y-interval reject first (cheap), then 2D SAT on the two
// yaw'd XZ rectangles: 4 candidate axes (each box's local x/z), comparing the centre-delta
// projection against the summed projected half-extents. Shared by deployable placement (ghost
// clip + deployable-vs-deployable overlap), the builder's deployable-clearance gate, and the door
// swing-arc checks. Pure scalar math, zero allocation. Touching faces read as separated (>=), so
// flush-seated furniture stays legal.
export function obbOverlap(
  acx: number, acy: number, acz: number, ahx: number, ahy: number, ahz: number, ayaw: number,
  bcx: number, bcy: number, bcz: number, bhx: number, bhy: number, bhz: number, byaw: number,
): boolean {
  if (Math.abs(acy - bcy) >= ahy + bhy) return false;
  const dx = bcx - acx, dz = bcz - acz;
  const ac = Math.cos(ayaw), as = Math.sin(ayaw);
  const bc = Math.cos(byaw), bs = Math.sin(byaw);
  // Box local axes in world XZ (the rotXZ / pointInBox convention): x = (cos, -sin), z = (sin, cos).
  // Between two frames only the relative angle matters: |cos| and |sin| of (ayaw - byaw).
  const cd = Math.abs(ac * bc + as * bs);
  const sd = Math.abs(as * bc - ac * bs);
  if (Math.abs(dx * ac - dz * as) >= ahx + bhx * cd + bhz * sd) return false; // axis: a's local x
  if (Math.abs(dx * as + dz * ac) >= ahz + bhx * sd + bhz * cd) return false; // axis: a's local z
  if (Math.abs(dx * bc - dz * bs) >= bhx + ahx * cd + ahz * sd) return false; // axis: b's local x
  if (Math.abs(dx * bs + dz * bc) >= bhz + ahx * sd + ahz * cd) return false; // axis: b's local z
  return true;
}

// Resolve a vertical-cylinder player (feet at pos.y, given height and radius) against the
// terrain (y = 0) and a set of boxes. Mutates pos and vel; returns whether grounded.
// Cylinder is approximated as a box of half-width = radius; each box is resolved along its
// axis of least penetration (a couple of passes for stability).
export function resolvePlayer(boxes: Box[], pos: THREE.Vector3, vel: THREE.Vector3, radius: number, height: number, stepHeight = 0): boolean {
  let grounded = false;

  const floorY = terrainHeight(pos.x, pos.z);
  if (pos.y < floorY) {
    pos.y = floorY;
    if (vel.y < 0) vel.y = 0;
    grounded = true;
  }

  // Step-up assist: walking into a low ledge (within stepHeight) climbs onto it instead of
  // being blocked. Lets you walk forgivingly onto the lowest foundation tier and into doorways.
  if (stepHeight > 0 && vel.y <= 0.5) {
    let stepTo = pos.y;
    for (const b of boxes) {
      const top = b.cy + b.hy;
      if (top <= pos.y + 0.05 || top > pos.y + stepHeight) continue;
      const cos = Math.cos(b.yaw);
      const sin = Math.sin(b.yaw);
      const lx = cos * (pos.x - b.cx) - sin * (pos.z - b.cz);
      const lz = sin * (pos.x - b.cx) + cos * (pos.z - b.cz);
      if (radius + b.hx - Math.abs(lx) > 0 && radius + b.hz - Math.abs(lz) > 0) {
        stepTo = Math.max(stepTo, top);
      }
    }
    if (stepTo > pos.y) {
      pos.y = stepTo;
      if (vel.y < 0) vel.y = 0;
      grounded = true;
    }
  }

  for (let pass = 0; pass < 2; pass++) {
    for (const b of boxes) {
      const cos = Math.cos(b.yaw);
      const sin = Math.sin(b.yaw);
      const dx = pos.x - b.cx;
      const dz = pos.z - b.cz;
      // World -> box-local (rotate by -yaw).
      const lx = cos * dx - sin * dz;
      const lz = sin * dx + cos * dz;

      const penX = radius + b.hx - Math.abs(lx);
      const penZ = radius + b.hz - Math.abs(lz);
      const pcy = pos.y + height / 2;
      const dy = pcy - b.cy;
      const penY = height / 2 + b.hy - Math.abs(dy);
      if (penX <= 0 || penZ <= 0 || penY <= 0) continue;

      if (penY <= penX && penY <= penZ) {
        // Vertical: land on top or bonk the underside.
        if (dy >= 0) {
          pos.y += penY;
          if (vel.y < 0) vel.y = 0;
          grounded = true;
        } else {
          pos.y -= penY;
          if (vel.y > 0) vel.y = 0;
        }
      } else if (penX <= penZ) {
        const dir = lx >= 0 ? 1 : -1;
        pos.x += cos * dir * penX;
        pos.z += -sin * dir * penX;
        const nx = cos * dir;
        const nz = -sin * dir;
        const vn = vel.x * nx + vel.z * nz;
        if (vn < 0) { vel.x -= vn * nx; vel.z -= vn * nz; }
      } else {
        const dir = lz >= 0 ? 1 : -1;
        pos.x += sin * dir * penZ;
        pos.z += cos * dir * penZ;
        const nx = sin * dir;
        const nz = cos * dir;
        const vn = vel.x * nx + vel.z * nz;
        if (vn < 0) { vel.x -= vn * nx; vel.z -= vn * nz; }
      }
    }
  }

  return grounded;
}

// How tall the player can stand at (x,z) before their head enters a box: the gap from feetY up to the
// lowest box that overlaps the player's cylinder above the feet, capped at maxRise. Used to FORCE crouch
// (and hard-clamp body height) in low spaces so the head can never pop up through a ceiling/wall.
export function headroom(boxes: Box[], x: number, z: number, feetY: number, radius: number, maxRise: number): number {
  let room = maxRise;
  for (const b of boxes) {
    const bottom = b.cy - b.hy;
    if (bottom <= feetY + 0.05 || bottom - feetY >= room) continue; // not above the feet / no closer than known
    const cos = Math.cos(b.yaw), sin = Math.sin(b.yaw);
    const dx = x - b.cx, dz = z - b.cz;
    const lx = cos * dx - sin * dz;
    const lz = sin * dx + cos * dz;
    if (radius + b.hx - Math.abs(lx) <= 0 || radius + b.hz - Math.abs(lz) <= 0) continue; // no XZ overlap
    room = bottom - feetY;
  }
  return room;
}

// First intersection parameter t in [0,1] of the segment p0->p1 with an oriented box, or -1 if it
// misses. Slab method in the box's local space (same world->local yaw rotation resolvePlayer uses).
// The bullet ray-march calls this each sub-step so rounds stop on walls, foundations and cover.
export function segmentBoxT(p0: THREE.Vector3, p1: THREE.Vector3, b: Box): number {
  const cos = Math.cos(b.yaw), sin = Math.sin(b.yaw);
  let dx = p0.x - b.cx, dz = p0.z - b.cz;
  const ox = cos * dx - sin * dz, oy = p0.y - b.cy, oz = sin * dx + cos * dz;
  dx = p1.x - b.cx; dz = p1.z - b.cz;
  const ex = cos * dx - sin * dz, ey = p1.y - b.cy, ez = sin * dx + cos * dz;
  const ddx = ex - ox, ddy = ey - oy, ddz = ez - oz;
  let tmin = 0, tmax = 1;
  if (Math.abs(ddx) < 1e-9) { if (Math.abs(ox) > b.hx) return -1; }
  else { let t1 = (-b.hx - ox) / ddx, t2 = (b.hx - ox) / ddx; if (t1 > t2) { const t = t1; t1 = t2; t2 = t; } if (t1 > tmin) tmin = t1; if (t2 < tmax) tmax = t2; if (tmin > tmax) return -1; }
  if (Math.abs(ddy) < 1e-9) { if (Math.abs(oy) > b.hy) return -1; }
  else { let t1 = (-b.hy - oy) / ddy, t2 = (b.hy - oy) / ddy; if (t1 > t2) { const t = t1; t1 = t2; t2 = t; } if (t1 > tmin) tmin = t1; if (t2 < tmax) tmax = t2; if (tmin > tmax) return -1; }
  if (Math.abs(ddz) < 1e-9) { if (Math.abs(oz) > b.hz) return -1; }
  else { let t1 = (-b.hz - oz) / ddz, t2 = (b.hz - oz) / ddz; if (t1 > t2) { const t = t1; t1 = t2; t2 = t; } if (t1 > tmin) tmin = t1; if (t2 < tmax) tmax = t2; if (tmin > tmax) return -1; }
  return tmin;
}

// First intersection t in [0,1] of the segment p0->p1 with a sphere, or -1. Tests rounds against
// target bodies (a sphere at chest height).
export function segmentSphereT(p0: THREE.Vector3, p1: THREE.Vector3, cx: number, cy: number, cz: number, r: number): number {
  const dx = p1.x - p0.x, dy = p1.y - p0.y, dz = p1.z - p0.z;
  const a = dx * dx + dy * dy + dz * dz;
  if (a < 1e-12) return -1;
  const fx = p0.x - cx, fy = p0.y - cy, fz = p0.z - cz;
  const b = 2 * (fx * dx + fy * dy + fz * dz);
  const c = fx * fx + fy * fy + fz * fz - r * r;
  let disc = b * b - 4 * a * c;
  if (disc < 0) return -1;
  disc = Math.sqrt(disc);
  const t1 = (-b - disc) / (2 * a);
  if (t1 >= 0 && t1 <= 1) return t1;
  const t2 = (-b + disc) / (2 * a);
  if (t2 >= 0 && t2 <= 1) return t2; // segment started inside the sphere
  return -1;
}
